import React, { useEffect, useState } from "react";
import { UserSettings } from "../../types/index";
import { DEFAULT_HANDOFF_KEYWORDS } from "../../lib/customer";

interface HandoffConfigCardProps {
  preferences: UserSettings;
  onUpdate: (preferences: UserSettings) => void;
  flushRef?: React.MutableRefObject<(() => UserSettings) | null>;
}

function keywordsToText(keywords?: string[]): string {
  return (keywords ?? DEFAULT_HANDOFF_KEYWORDS).join(", ");
}

function parseKeywords(text: string): string[] {
  return text
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
}

const HandoffConfigCard: React.FC<HandoffConfigCardProps> = ({
  preferences,
  onUpdate,
  flushRef,
}) => {
  const [keywordsText, setKeywordsText] = useState(() =>
    keywordsToText(preferences.handoffKeywords),
  );

  useEffect(() => {
    setKeywordsText(keywordsToText(preferences.handoffKeywords));
  }, [preferences.handoffKeywords]);

  const commitKeywords = (): UserSettings => {
    const updated = {
      ...preferences,
      handoffKeywords: parseKeywords(keywordsText),
    };
    onUpdate(updated);
    return updated;
  };

  useEffect(() => {
    if (flushRef) {
      flushRef.current = commitKeywords;
    }
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-semibold text-primary mb-4">
        Transferencia a humano
      </h2>
      <p className="text-gray-600 mb-4">
        Define cómo el asistente debe transferir conversaciones de WhatsApp a
        una persona. Responde desde la app de WhatsApp en tu teléfono cuando un
        cliente esté en modo humano.
      </p>

      <div className="mb-4 flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div>
          <h3 className="font-semibold text-gray-900">Activar transferencia</h3>
          <p className="text-sm text-gray-600">
            Permite transferir conversaciones a una persona automáticamente por
            palabras clave o por instrucción del asistente.
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={preferences.humanTransferEnabled}
            onChange={(e) =>
              onUpdate({
                ...preferences,
                humanTransferEnabled: e.target.checked,
              })
            }
            className="peer sr-only"
          />
          <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-primary peer-checked:after:translate-x-5" />
        </label>
      </div>

      <div className="mb-4">
        <label
          htmlFor="handoffInstructions"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Instrucciones para el asistente
        </label>
        <textarea
          id="handoffInstructions"
          rows={4}
          value={preferences.handoffInstructions ?? ""}
          disabled={!preferences.humanTransferEnabled}
          onChange={(e) =>
            onUpdate({ ...preferences, handoffInstructions: e.target.value })
          }
          placeholder="Para conectar con un representante: se necesita tener el nombre y correo."
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
        />
      </div>

      <div>
        <label
          htmlFor="handoffKeywords"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Palabras clave (separadas por coma)
        </label>
        <input
          id="handoffKeywords"
          type="text"
          value={keywordsText}
          disabled={!preferences.humanTransferEnabled}
          onChange={(e) => setKeywordsText(e.target.value)}
          onBlur={commitKeywords}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
        />
        <p className="mt-2 text-sm text-gray-500">
          Si un mensaje contiene alguna palabra clave, la conversación pasa a
          humano de inmediato y se envía un aviso automático al cliente. Sin
          palabras clave, solo se transfiere cuando el asistente decide
          solicitarlo.
        </p>
      </div>
    </div>
  );
};

export default HandoffConfigCard;
